class GlobalProductCard {
  constructor(element) {
    this.el = element;
    this.imagePrimary = this.el.querySelector('.uc-product-card__slide[data-slide-index="0"]');
    this.cardUrl = this.el.querySelector('[data-card-url]');
    this.cardTitleUrl = this.el.querySelector('[data-card-title-url]');
    this.cardPrice = this.el.querySelector('[data-card-price]');
    this.cardInstallment = this.el.querySelector('[data-card-installment]');
    this.cardColor = this.el.querySelector('[data-card-color]');
    this.vtoTriggers = [...this.el.querySelectorAll('[data-vto-trigger]')];
    this.shopNowBtn = this.el.querySelector('[data-shop-now]');
    this.notifyBtn = this.el.querySelector('.CollectionSliderCard__IconOnlyBtn--notify');
    this.badgeTagNode = this.el.querySelector('[data-variant-badge-tag]');
    this.badgeSoldOutNode = this.el.querySelector('[data-variant-badge-sold-out]');
    this.swatches = [...this.el.querySelectorAll('[data-swatch]')];
    this.swatchRadios = [...this.el.querySelectorAll('.ColorSwatch__Radio')];
    this.swiperEl = this.el.querySelector('[data-card-swiper]');
    this.paginationEl = this.el.querySelector('[data-card-pagination]');
    this.swiper = null;
    this.slides = [...this.el.querySelectorAll('.swiper-slide[data-media-id]')];
    this.activeSlideIndex = 0;
    this.hoverStartTimer = null;
    this.hoverAutoplayDelayMs = 2000;

    // Cache all original slides so we can filter per variant.
    this.__allSlidesCache = null;
    this.ensureAllSlidesCache();

    this.bindEvents();

    // Sync CTA + notify to the default active swatch (matches collection card color / variant)
    const initialSwatch = this.getActiveSwatch();
    const initialSoldOut = initialSwatch?.dataset?.variantIsSoldOut === 'true';
    this.updateShopNowState(initialSoldOut, initialSwatch?.dataset?.variantUrl);
    this.updateNotifyState(initialSoldOut, initialSwatch?.dataset?.variantId);

    if (initialSwatch) {
      this.applyVariantMediaRange(initialSwatch);
    }
  }

  getActiveSwatch() {
    return this.el.querySelector('[data-swatch].uc-is-active') || this.swatches[0] || null;
  }

  parseMediaIds(rawValue) {
    if (!rawValue || typeof rawValue !== 'string') return [];
    return rawValue
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }

  ensureAllSlidesCache() {
    if (this.__allSlidesCache) return;
    if (!this.swiperEl) {
      this.__allSlidesCache = [];
      return;
    }
    const slides = [...this.swiperEl.querySelectorAll('.swiper-slide[data-media-id]')];
    this.__allSlidesCache = slides.map((slide) => ({
      id: (slide.getAttribute('data-media-id') || '').trim(),
      html: slide.outerHTML
    }));
  }

  initSwiper() {
    if (!this.swiperEl || !this.paginationEl) return;
    if (this.swiperEl.swiper) {
      this.swiper = this.swiperEl.swiper;
      return;
    }
    if (typeof window.Swiper === 'undefined') return;

    try {
      const loop = this.paginationEl.style.display === 'none' ? false : true;
      this.swiper = new window.Swiper(this.swiperEl, {
        nested: true,
        allowTouchMove: loop,
        observer: true,
        observeParents: true,
        effect: 'fade',
        fadeEffect: { crossFade: true },
        loop,
        pagination: {
          el: this.paginationEl,
          clickable: true,
          bulletClass: 'uc-product-card__dot',
          bulletActiveClass: 'is-active',
          renderBullet: function (index, className) {
            return `<span class="${className}" data-index="${index}"></span>`;
          }
        },
        autoplay: {
          delay: 1400,
          disableOnInteraction: false,
          pauseOnMouseEnter: false,
          stopOnLastSlide: false
        }
      });

      // Start stopped; hover will enable autoplay (desktop).
      if (this.swiper.autoplay) this.swiper.autoplay.stop();

      const syncDotsToRealIndex = () => {
        if (!this.paginationEl) return;
        const dots = [...this.paginationEl.querySelectorAll('.uc-product-card__dot')];
        if (!dots.length) return;
        const real = Number(this.swiper?.realIndex || 0);
        dots.forEach((dot, idx) => {
          dot.classList.toggle('is-active', idx === real);
        });
      };

      // Swiper pagination sometimes momentarily lags on loop-wrap with fade.
      // Keep dots aligned to realIndex deterministically.
      this.swiper.on('realIndexChange', () => {
        this.activeSlideIndex = this.swiper.realIndex || 0;
        syncDotsToRealIndex();
      });
      this.swiper.on('slideChangeTransitionEnd', syncDotsToRealIndex);
      syncDotsToRealIndex();
    } catch (e) {
      this.swiper = null;
    }
  }

  setSlidesHtml(slideHtmlArray, targetIndex) {
    if (!this.swiperEl) return;
    const wrapper = this.swiperEl.querySelector('.swiper-wrapper');
    if (!wrapper) return;
    wrapper.innerHTML = slideHtmlArray.join('');
    this.slides = [...wrapper.querySelectorAll('.swiper-slide[data-media-id]')];
    this.imagePrimary = this.el.querySelector('.uc-product-card__slide[data-slide-index="0"]');
    this.activeSlideIndex = Math.max(0, targetIndex || 0);
  }

  applyVariantMediaRange(swatch) {
    if (!swatch) return;
    this.ensureAllSlidesCache();
    if (!this.__allSlidesCache || !this.__allSlidesCache.length) return;

    const allowedIds = this.parseMediaIds(swatch.dataset.mediaIds);
    const selectedMediaId = (swatch.dataset.firstMediaId || '').trim() || '';
    const currentHeroMediaId = (this.imagePrimary?.getAttribute('data-media-id') || '').trim();
    const byId = (id) => this.__allSlidesCache.find((item) => item.id === id);

    let filtered = [];
    if (allowedIds.length) {
      const allowedSet = new Set(allowedIds);
      filtered = this.__allSlidesCache.filter((item) => allowedSet.has(item.id));
    }
    if (!filtered.length && selectedMediaId) {
      const selectedMedia = byId(selectedMediaId);
      if (selectedMedia) filtered = [selectedMedia];
    }
    if (!filtered.length && currentHeroMediaId) {
      const heroMedia = byId(currentHeroMediaId);
      if (heroMedia) filtered = [heroMedia];
    }
    if (!filtered.length) {
      filtered = [this.__allSlidesCache[0]];
    }

    // Toggle pagination visibility based on filtered set size
    if (this.paginationEl) {
      this.paginationEl.style.display = filtered.length > 1 ? '' : 'none';
    }

    let targetIndex = 0;
    if (selectedMediaId) {
      const idx = filtered.findIndex((item) => item.id === selectedMediaId);
      if (idx >= 0) targetIndex = idx;
    }

    const slideHtml = filtered.map((item) => item.html);
    this.initSwiper();
    const shouldLoop = filtered.length > 1;

    if (!this.swiper) {
      // No swiper yet: update markup only.
      this.setSlidesHtml(slideHtml, targetIndex);
      return;
    }

    const swiperInstance = this.swiper;
    const wasAutoplayRunning = Boolean(swiperInstance.autoplay && swiperInstance.autoplay.running);
    if (swiperInstance.autoplay) swiperInstance.autoplay.stop();

    // If loop mode needs to change, rebuild Swiper to keep pagination + indices consistent.
    if (swiperInstance.params.loop !== shouldLoop) {
      try {
        swiperInstance.destroy(true, false);
      } catch (e) {}
      this.swiper = null;
      this.setSlidesHtml(slideHtml, targetIndex);
      this.initSwiper();
    } else {
      try {
        swiperInstance.removeAllSlides();
        swiperInstance.appendSlide(slideHtml);
        swiperInstance.update();
        if (swiperInstance.pagination) {
          swiperInstance.pagination.render();
          swiperInstance.pagination.update();
        }
      } catch (e) {
        // Fallback to DOM update if Swiper slide mutation fails
        this.setSlidesHtml(slideHtml, targetIndex);
        try {
          swiperInstance.update();
        } catch (err) {}
      }
    }

    if (this.swiper) {
      if (this.swiper.params.loop && typeof this.swiper.slideToLoop === 'function') {
        this.swiper.slideToLoop(targetIndex, 0, false);
      } else {
        this.swiper.slideTo(targetIndex, 0, false);
      }
      if (wasAutoplayRunning && this.swiper.autoplay) {
        this.swiper.autoplay.start();
      }
    }
  }

  bindEvents() {
    if (this.paginationEl) {
      this.paginationEl.addEventListener('click', (event) => {
        // Pagination sits inside the card link; never let clicks navigate to PDP.
        event.preventDefault();
        event.stopPropagation();

        const dot = event.target.closest('.uc-product-card__dot');
        if (!dot) return;

        const index = Number(dot.dataset.index);
        if (!Number.isFinite(index) || index < 0) return;

        this.initSwiper();
        if (!this.swiper) return;

        if (this.swiper.params.loop && typeof this.swiper.slideToLoop === 'function') {
          this.swiper.slideToLoop(index);
        } else {
          this.swiper.slideTo(index);
        }
      });
    }

    this.swatches.forEach((swatch) => {
      swatch.addEventListener('click', () => this.onSwatchSelect(swatch));
    });

    if (this.vtoTriggers.length) {
      this.vtoTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const sku = trigger.dataset.vtoSku;
          if (sku && window.SprieSDK && typeof window.SprieSDK.Load === 'function') {
            window.SprieSDK.Load(sku);
            return;
          }
          const fallbackVtoEvent = new CustomEvent('unified:vto', {
            detail: { sku, handle: this.el.dataset.productHandle }
          });
          window.dispatchEvent(fallbackVtoEvent);
        });
      });
    }

    if (this.shopNowBtn) {
      this.shopNowBtn.addEventListener('click', () => {
        const productId = this.shopNowBtn.dataset.productId;
        const productHandle = this.shopNowBtn.dataset.productHandle;
        const variantUrl = this.shopNowBtn.dataset.variantUrl || this.cardUrl?.getAttribute('href');
        const isSoldOut = this.shopNowBtn.dataset.variantIsSoldOut === 'true';

        if (isSoldOut) {
          if (variantUrl) {
            window.location.href = variantUrl;
            return;
          }
          if (productHandle) {
            window.location.href = `/products/${productHandle}`;
          }
          return;
        }

        if (window.openQuickShop && productId) {
          window.openQuickShop(productId, this.shopNowBtn);
          return;
        }

        const fallbackUrl = this.cardUrl?.getAttribute('href');
        if (fallbackUrl) {
          window.location.href = fallbackUrl;
          return;
        }

        if (productHandle) {
          window.location.href = `/products/${productHandle}`;
        }
      });
    }

    // Ensure Swiper exists early so swipe + pagination work immediately.
    this.initSwiper();

    this.el.addEventListener('mouseenter', () => this.startSlider());
    this.el.addEventListener('mouseleave', (event) => {
      const relatedTarget = event.relatedTarget;
      if (relatedTarget && this.el.contains(relatedTarget)) return;
      this.stopSlider();
    });
  }

  onSwatchSelect(swatch) {
    // Stop hover autoplay before mutating slides
    this.stopSlider();

    this.swatches.forEach((item) => {
      item.classList.remove('uc-is-active');
      item.setAttribute('aria-checked', 'false');
      item.setAttribute('tabindex', '-1');
    });
    swatch.classList.add('uc-is-active');
    swatch.setAttribute('aria-checked', 'true');
    swatch.setAttribute('tabindex', '0');

    const variantUrl = swatch.dataset.variantUrl;
    const variantImage = swatch.dataset.image;
    const variantPrice = swatch.dataset.price;
    const variantInstallment = swatch.dataset.installment;
    const colorName = swatch.dataset.colorName;
    const variantSku = swatch.dataset.variantSku;
    const variantBadgeText = swatch.dataset.variantBadgeText || '';
    const variantSoldOutText = swatch.dataset.variantSoldOutText || '';
    const variantIsSoldOut = swatch.dataset.variantIsSoldOut === 'true';

    if (variantUrl) {
      this.cardUrl?.setAttribute('href', variantUrl);
      this.cardTitleUrl?.setAttribute('href', variantUrl);
    }
    this.updateShopNowState(variantIsSoldOut, variantUrl);
    this.updateNotifyState(variantIsSoldOut, swatch.dataset.variantId);

    if (this.swatchRadios.length) {
      this.swatchRadios.forEach((radio) => {
        const isSelected = radio.dataset.variantId === swatch.dataset.variantId;
        radio.checked = isSelected;
      });
    }

    if (variantImage && this.imagePrimary) {
      this.imagePrimary.setAttribute('src', variantImage);
    }

    // Update gallery: show only selected variant images and loop them.
    this.applyVariantMediaRange(swatch);

    if (variantPrice && this.cardPrice) {
      this.cardPrice.textContent = variantPrice;
    }

    if (variantInstallment && this.cardInstallment) {
      this.cardInstallment.textContent = variantInstallment;
    }

    if (colorName && this.cardColor) {
      this.cardColor.textContent = colorName;
    }

    if (variantSku && this.vtoTriggers.length) {
      this.vtoTriggers.forEach((trigger) => {
        trigger.dataset.vtoSku = variantSku;
      });
    }

    if (this.badgeTagNode) {
      const firstTag = variantBadgeText.split('||').map((item) => item.trim()).filter(Boolean)[0] || '';
      const shouldShowTag = !!firstTag && !variantIsSoldOut;
      this.badgeTagNode.textContent = firstTag;
      this.badgeTagNode.style.display = shouldShowTag ? '' : 'none';
    }

    if (this.badgeSoldOutNode) {
      this.badgeSoldOutNode.textContent = variantSoldOutText || 'Sold Out';
      this.badgeSoldOutNode.style.display = variantIsSoldOut ? '' : 'none';
    }
  }

  updateShopNowState(isSoldOut, variantUrl) {
    if (!this.shopNowBtn) return;
    this.shopNowBtn.dataset.variantIsSoldOut = isSoldOut ? 'true' : 'false';
    if (variantUrl) {
      this.shopNowBtn.dataset.variantUrl = variantUrl;
    }
    this.shopNowBtn.textContent = isSoldOut ? 'Product Details' : 'Shop now';
    this.shopNowBtn.classList.toggle('is-sold-out', isSoldOut);
  }

  updateNotifyState(isSoldOut, variantId) {
    if (!this.notifyBtn) return;
    this.notifyBtn.style.display = isSoldOut ? '' : 'none';
    if (variantId) {
      this.notifyBtn.dataset.variantId = variantId;
    }
  }

  showSlide(index) {
    if (this.swiper) {
      const safeIndex = Math.max(0, Math.min(index, (this.swiper.slides?.length || 1) - 1));
      this.swiper.slideTo(safeIndex);
      return;
    }
  }

  startSlider() {
    if (window.innerWidth < 1024) return;
    window.clearTimeout(this.hoverStartTimer);
    // Ensure hover autoplay starts from currently selected variant range.
    const active = this.getActiveSwatch();
    if (active) this.applyVariantMediaRange(active);

    this.hoverStartTimer = window.setTimeout(() => {
      this.hoverStartTimer = null;
      if (window.innerWidth < 1024) return;
      if (!this.el.matches(':hover')) return;
      if (this.swiper && this.swiper.autoplay && (this.swiper.slides?.length || 0) > 1) {
        this.swiper.autoplay.start();
      }
    }, this.hoverAutoplayDelayMs);
  }

  stopSlider() {
    window.clearTimeout(this.hoverStartTimer);
    this.hoverStartTimer = null;
    if (this.swiper && this.swiper.autoplay) {
      this.swiper.autoplay.stop();
    }
    // When hover ends, return to selected variant featured image.
    const active = this.getActiveSwatch();
    if (active) this.applyVariantMediaRange(active);
  }
}

class GlobalProductCardRegistry {
  constructor() {
    this.init();
    document.addEventListener('unified:grid-updated', (event) => {
      if (event?.detail?.grid) {
        this.init(event.detail.grid);
      }
    });
  }

  init(scope = document) {
    const cards = scope.querySelectorAll('[data-product-card]:not([data-card-ready])');
    cards.forEach((card) => {
      card.dataset.cardReady = 'true';
      new GlobalProductCard(card);
    });
  }
}

window.GlobalProductCardRegistry = window.GlobalProductCardRegistry || new GlobalProductCardRegistry();
